import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getAuth, onAuthStateChanged, connectAuthEmulator } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { initializeApp, getApps, getApp } from "firebase/app";
import { ReCaptchaV3Provider, initializeAppCheck, getToken } from 'firebase/app-check';
import { getFirestore, connectFirestoreEmulator, enableNetwork, disableNetwork, enableMultiTabIndexedDbPersistence, initializeFirestore } from 'firebase/firestore';

const FirebaseContext = createContext();

// Initialize Firebase services once outside component to prevent re-initialization
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Check if Firebase app already exists to prevent duplicate initialization
let app;
const existingApps = getApps();
if (existingApps.length > 0) {
  // Use existing Firebase app
  app = getApp();
} else {
  // Initialize new Firebase app
  app = initializeApp(firebaseConfig);
}
const auth = getAuth(app);
const storage = getStorage(app);

// Initialize Firestore with settings to prevent excessive Listen channel connections
let db;
try {
  // Use initializeFirestore with settings to control connections
  db = initializeFirestore(app, {
    // Prevent excessive WebSocket Listen channel connections
    cacheSizeBytes: 100000000, // 100MB cache
    // Only force long polling if we continue to see connection issues
    ...(process.env.NODE_ENV === 'production' && { 
      experimentalAutoDetectLongPolling: true 
    })
  });
} catch (error) {
  console.warn('initializeFirestore failed, falling back to getFirestore:', error);
  // Fallback to standard initialization
  db = getFirestore(app);
}

// Connect to emulators once if running on localhost or if emulator environment is detected
const shouldUseEmulator = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ||
  process.env.NODE_ENV === 'development';

if (shouldUseEmulator) {
  try {
    connectFirestoreEmulator(db, 'localhost', 8081);
    connectAuthEmulator(auth, 'http://localhost:9099');
    console.log('🔧 Connected to Firebase emulators');
  } catch (error) {
    console.warn('Emulator connection failed (may already be connected):', error.message);
  }
}

// Enable offline persistence once if not using emulators
if (!shouldUseEmulator) {
  enableMultiTabIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Firestore persistence failed: Multiple tabs open, persistence can only be enabled in one tab at a time.');
    } else if (err.code === 'unimplemented') {
      console.warn('Firestore persistence not supported by this browser.');
    }
  });
}

export function FirebaseProvider({ children }) {
  // Firebase services are now initialized once outside the component

  // Initialize appCheck with debug mode in development
  let appCheck = null;
  
  // Check if AppCheck should be disabled
  const disableAppCheck = process.env.REACT_APP_DISABLE_APP_CHECK === 'true';
  
  if (!disableAppCheck) {
    if (process.env.NODE_ENV === 'production') {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(
          process.env.REACT_APP_FIREBASE_RECAPTCHA_SITE_KEY,
        ),
        isTokenAutoRefreshEnabled: true
      });
    } else {
      // In development, use debug token provider
      // Add this to your .env.development file: REACT_APP_FIREBASE_APP_CHECK_DEBUG_TOKEN=your-debug-token
      const debugToken = process.env.REACT_APP_FIREBASE_APP_CHECK_DEBUG_TOKEN;
      
      // Enable debug mode in the console (only in development)
      if (typeof window !== 'undefined') {
        window.self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
      }
      
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(
          process.env.REACT_APP_FIREBASE_RECAPTCHA_SITE_KEY,
        ),
        isTokenAutoRefreshEnabled: true
      });
    }
  } else {
    console.log('AppCheck disabled via REACT_APP_DISABLE_APP_CHECK environment variable');
  }

  const getSecureHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    
    // Add App Check token if available
    if (appCheck) {
      try {
        const { token: appCheckToken } = await getToken(appCheck, true);
        headers['X-Firebase-AppCheck'] = appCheckToken;
      } catch (error) {
        console.error('Error getting AppCheck token:', error);
      }
    }
    
    // Add Firebase Auth token if user is logged in
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const idToken = await currentUser.getIdToken(false);
        headers['Authorization'] = `Bearer ${idToken}`;
      } catch (error) {
        console.error('Error getting Auth token:', error);
        
        // If quota exceeded, try without forcing refresh as fallback
        if (error.code === 'auth/quota-exceeded') {
          console.warn('Auth quota exceeded, using cached token');
          try {
            const cachedToken = await currentUser.getIdToken(false);
            headers['Authorization'] = `Bearer ${cachedToken}`;
          } catch (fallbackError) {
            console.error('Failed to get cached token:', fallbackError);
          }
        }
      }
    }
    
    return headers;
  }, [appCheck]);

  const value = useMemo(() => ({
    app,
    auth,
    storage,
    db,
    appCheck,
    onAuthStateChanged,
    getSecureHeaders,
    enableNetwork: () => enableNetwork(db),
    disableNetwork: () => disableNetwork(db)
  }), [appCheck, getSecureHeaders]);

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
}

export function useFirebase() {
  return useContext(FirebaseContext);
}
